import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Chip,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Star as StarIcon
} from '@mui/icons-material';
import FavoriteTeamService from '../services/favoriteTeamService';
import TeamBadge from './TeamBadge';
import { useAuth } from '../contexts/AuthContext';

const FavoriteTeamsBySport = ({ 
  disabled = false,
  onChange
}) => {
  const { currentUser } = useAuth();
  const [teamsBySport, setTeamsBySport] = useState({});
  const [favoriteTeams, setFavoriteTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load teams grouped by sport
      const grouped = FavoriteTeamService.getTeamsGroupedBySport();
      setTeamsBySport(grouped);
      
      // Load current favorite teams
      const currentFavorites = await FavoriteTeamService.getFavoriteTeams();
      setFavoriteTeams(currentFavorites);
      
    } catch (error) {
      console.error('Error loading favorite teams data:', error);
      setError('Failed to load favorite teams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = async (sport, team) => {
    try {
      const teamName = team?.name || null;
      const success = await FavoriteTeamService.setFavoriteTeamForSport(sport, teamName);
      
      if (success) {
        setFavoriteTeams(prev => ({
          ...prev,
          [sport]: teamName
        }));
        
        // Notify parent component if provided
        if (onChange) {
          onChange(sport, team);
        }
      }
    } catch (error) {
      console.error('Error updating favorite team:', error);
    }
  };

  // Component to render team option with its color badge
  const TeamOption = ({ team, size = 24 }) => {
    if (!team) return null;

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <TeamBadge teamName={team.name} size={size} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {team.name}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Loading favorite teams...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {Object.entries(teamsBySport).map(([sport, teams]) => {
          const currentFavorite = favoriteTeams[sport];
          const favoriteTeamObj = currentFavorite 
            ? teams.find(t => t.name === currentFavorite)
            : null;

          return (
            <Box key={sport}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 0.5 }}>
                {sport}
              </Typography>
              
              <Autocomplete
                value={favoriteTeamObj}
                onChange={(event, newValue) => handleTeamChange(sport, newValue)}
                options={teams}
                getOptionLabel={(option) => option?.name || ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <TeamOption team={option} />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Select ${sport} Favorite`}
                    placeholder={`Choose your favorite ${sport} team...`}
                    size="medium"
                  />
                )}
                disabled={disabled}
                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: favoriteTeamObj ? 'success.main' : 'primary.main'
                    }
                  }
                }}
              />
              
              {favoriteTeamObj && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
                  <TeamOption team={favoriteTeamObj} size={32} />
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default FavoriteTeamsBySport;